/**
 * Zustand store for the pixel world scene state.
 */

import { create } from "zustand";

export type AgentStatus = "idle" | "running" | "waiting" | "error" | "terminated";
export type EdgeType = "delegates-to" | "reports-to" | "peers-with" | "pipes-to" | "custom";

export interface PendingMessage {
  id: string;
  fromAgentId: string;
  toAgentId: string;
  edgeType: EdgeType;
  timestamp: number;
}

interface PixelWorldState {
  /** Whether the scene has been built and is ready to render. */
  sceneReady: boolean;

  /** Current topology being displayed. */
  topologyId: string | null;
  topologyType: string | null;

  /** Agent statuses keyed by agent ID. */
  agentStatuses: Map<string, AgentStatus>;

  /** Messages waiting to be animated. */
  pendingMessages: PendingMessage[];

  /** Counter for generating unique message IDs. */
  msgCounter: number;

  // Actions
  setScene: (topologyId: string, topologyType: string) => void;
  setAgentStatus: (agentId: string, status: AgentStatus) => void;
  enqueueMessage: (msg: Omit<PendingMessage, "id" | "timestamp">) => void;
  dequeueMessage: (id: string) => void;
  reset: () => void;
}

export const usePixelWorldStore = create<PixelWorldState>((set, get) => ({
  sceneReady: false,
  topologyId: null,
  topologyType: null,
  agentStatuses: new Map(),
  pendingMessages: [],
  msgCounter: 0,

  setScene: (topologyId, topologyType) =>
    set({
      topologyId,
      topologyType,
      sceneReady: true,
      agentStatuses: new Map(),
      pendingMessages: [],
    }),

  setAgentStatus: (agentId, status) =>
    set((state) => {
      const next = new Map(state.agentStatuses);
      next.set(agentId, status);
      return { agentStatuses: next };
    }),

  enqueueMessage: (msg) => {
    const counter = get().msgCounter + 1;
    set((state) => ({
      msgCounter: counter,
      pendingMessages: [
        ...state.pendingMessages,
        { ...msg, id: `msg-${counter}`, timestamp: Date.now() },
      ],
    }));
  },

  dequeueMessage: (id) =>
    set((state) => ({
      pendingMessages: state.pendingMessages.filter((m) => m.id !== id),
    })),

  reset: () =>
    set({
      sceneReady: false,
      topologyId: null,
      topologyType: null,
      agentStatuses: new Map(),
      pendingMessages: [],
    }),
}));
